import React, { useState } from 'react';
import {
  Users,
  Calendar,
  DollarSign,
  Award,
  ArrowRight,
  Clock,
  UserCheck,
  FileCheck,
  ShieldCheck,
  CheckCircle2,
  Terminal,
  Activity,
  Briefcase,
  GraduationCap
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HRMSolution = () => {
  const [activeModule, setActiveModule] = useState('payroll');

  const hrmPillars = [
    {
      icon: DollarSign,
      title: 'Automated Payroll & Tax Engine',
      badge: '100% Accuracy',
      color: 'from-cyan-500 to-blue-600',
      description: 'Automated direct deposit payouts, income tax withholding, statutory deductions (PF, ESI, 401k), and instant PDF paystub distribution.',
      bullets: ['1-Click Direct Deposit Payouts', 'Automated Income Tax Withholding', 'Instant PDF Paystub Distribution']
    },
    {
      icon: Clock,
      title: 'Biometric Attendance & Geofencing',
      badge: 'Real-Time Sync',
      color: 'from-purple-500 to-indigo-600',
      description: 'Integration with biometric facial/fingerprint hardware, mobile GPS geofenced clock-in, shift scheduling, and automated overtime calculations.',
      bullets: ['Biometric Hardware API Sync', 'Mobile GPS Geofenced Clock-In', 'Automated Overtime & Shift Rules']
    },
    {
      icon: UserCheck,
      title: 'Talent Acquisition & ATS Pipeline',
      badge: 'AI Resume Screen',
      color: 'from-orange-500 to-amber-500',
      description: 'Job board syndication, AI-assisted resume parsing, automated interview scheduling, and digital e-signature offer letters.',
      bullets: ['Job Board 1-Click Syndication', 'AI Resume Candidate Matching', 'Digital e-Signature Offer Letters']
    },
    {
      icon: Users,
      title: 'Employee Self-Service (ESS) Portal',
      badge: 'Mobile & Web App',
      color: 'from-emerald-500 to-teal-600',
      description: 'Empower staff to view payslips, submit leave requests, file expense claims, and update tax declarations via mobile app or web portal.',
      bullets: ['Mobile App & Web Portal Access', 'Leave & Expense Claim Approval', 'Tax Declaration & Benefit Upload']
    },
    {
      icon: Award,
      title: 'Performance OKRs & 360° Reviews',
      badge: 'Goal Tracking',
      color: 'from-pink-500 to-rose-600',
      description: 'Continuous performance management with quarterly OKR tracking, peer 360° feedback reviews, and promotion merit matrices.',
      bullets: ['Quarterly OKR & Goal Tracking', '360-Degree Peer Feedback Reviews', 'Promotion & Merit Increase Grid']
    },
    {
      icon: GraduationCap,
      title: 'Digital Onboarding & Compliance',
      badge: 'Zero Friction',
      color: 'from-blue-500 to-cyan-600',
      description: 'Automated digital onboarding workflows, document verification, compliance training courses, and employee sentiment surveys.',
      bullets: ['Automated Document Collection', 'Mandatory Compliance Training', 'Employee Engagement Surveys']
    }
  ];

  const moduleSpecs = {
    payroll: {
      name: 'Payroll & Tax Compensation Engine',
      desc: 'Eliminate manual spreadsheet payroll errors with automated tax calculations, bonus disbursements, and direct bank integration.',
      specs: ['Automated Tax Withholding Calculator', 'Multi-Bank Direct Deposit Sync', 'Reimbursement & Bonus Processing', 'Statutory Compliance Audit Trail']
    },
    attendance: {
      name: 'Biometric Attendance & Leave Management',
      desc: 'Track workforce attendance across office locations and remote teams with real-time biometric hardware sync and GPS geofencing.',
      specs: ['Facial / Fingerprint Hardware Sync', 'GPS Geofenced Mobile Punching', 'Automated Paid Time Off (PTO) Accrual', 'Shift Swap & Overtime Approval']
    },
    performance: {
      name: 'Performance OKRs & Talent Reviews',
      desc: 'Align team goals with enterprise objectives through structured 360-degree reviews, continuous feedback, and skill matrix tracking.',
      specs: ['Company & Team OKR Alignment', 'Custom Performance Review Templates', 'Continuous 1-on-1 Feedback Notes', '9-Box Talent Grid Matrix']
    }
  };

  const hrmSteps = [
    { step: '01', title: 'Employee Database & Org Structure Audit', desc: 'Consolidating employee records, department structures, salary grades, and benefit policies into a secure cloud vault.' },
    { step: '02', title: 'Biometric Hardware & API Synchronization', desc: 'Connecting office biometric devices, GPS mobile app clock-in, and calendar integrations.' },
    { step: '03', title: 'Payroll Rule & Tax Withholding Configuration', desc: 'Setting up local tax rules, overtime multipliers, leave accruals, and direct deposit bank accounts.' },
    { step: '04', title: 'Employee Self-Service Mobile Portal Launch', desc: 'Onboarding staff to the mobile app for instant payslip downloads, leave requests, and profile updates.' },
    { step: '05', title: 'Executive Workforce Analytics Deployment', desc: 'Configuring executive headcount dashboards, turnover telemetry, and 24/7 technical support.' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-6">
                <Users className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                  Next-Gen Cloud HRM & Payroll
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Smart Enterprise <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Human Resource Management</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Automate onboarding, biometric time attendance tracking, multi-region tax payroll, performance OKRs, and employee self-service portals in one unified cloud system.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Request HRM Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#hrm-pillars">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore Capabilities
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live HR Telemetry Console */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-sm font-mono text-emerald-400 font-bold">HRM ENGINE: ACTIVE</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">PAYROLL v5.0</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Active Staff</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">1,280</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Payroll Accuracy</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">100%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Attendance SLA</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">99.4%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Biometric Sync</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">ONLINE</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-purple-400" />
                  <span>hrm_payroll.dispatch_direct_deposit(#BATCH-802)</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* HRM Metrics Banner */}
      <section className="py-10 bg-gray-900/80 border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">60%+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">HR Admin Hours Saved</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">100%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Automated Tax Payroll</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">99.4%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Biometric Time Sync SLA</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">4.9 ★</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Employee Portal Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Grid */}
      <section id="hrm-pillars" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                Full-Lifecycle HRM Pillars
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Enterprise HRM <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Capabilities</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From 1-click tax payroll dispatch to biometric attendance, mobile ESS portal, and OKR goal reviews.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hrmPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-purple-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive HRM Module Selector */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Explore Enterprise HRM Modules
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              Modular architecture configured specifically for your payroll tax rules, time tracking hardware, or performance OKR framework.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActiveModule('payroll')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'payroll'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Payroll & Tax Engine
              </button>
              <button
                onClick={() => setActiveModule('attendance')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'attendance'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Biometric & Leave
              </button>
              <button
                onClick={() => setActiveModule('performance')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'performance'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Performance & OKRs
              </button>
            </div>
          </div>

          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              {moduleSpecs[activeModule].name}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {moduleSpecs[activeModule].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {moduleSpecs[activeModule].specs.map((spec, idx) => (
                <div key={idx} className="bg-gray-950/80 border border-gray-800 rounded-xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5-Step HRM Implementation Roadmap */}
      <section className="py-24 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              5-Step Enterprise HRM Deployment Roadmap
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              From employee database audit to biometric sync, payroll configuration, and mobile app launch.
            </p>
          </div>

          <div className="space-y-6">
            {hrmSteps.map((proc, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 hover:border-purple-500/50 transition-all">
                <span className="text-3xl font-black text-purple-400 font-mono flex-shrink-0">{proc.step}</span>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">{proc.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{proc.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width Executive Consultation CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-gray-800/80 w-full">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-900 via-gray-800 to-slate-900 border border-purple-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to Modernize Your HR & Payroll Operations?
            </h2>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              Schedule a technical session with our HRM system architects to plan your automated payroll, biometric time tracking, and employee self-service portal.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule Executive HRM Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HRMSolution;